import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  Snackbar,
  Alert,
  IconButton,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import ClaimType from "./ClaimType";
import PersonalInfo from "./PersonalInfo";
import IncidentDetails from "./IncidentDetails";
import Evidence from "./Evidence";
import Review from "./Review";
import {
  IPolicyDetailsHeader,
  IPolicyVehicle,
} from "../../models/user/policyDetails/PolicyDetails";
import { Policy } from "../../Agents/CustomerDetailsList/customerDetailsListType";
import ClaimFlowStyles from "./ClaimFlowStyles";

const steps = [
  "Claim Type",
  "Personal Info",
  "Incident Details",
  "Evidence",
  "Review",
];

interface ClaimFlowProps {
  onBack?: () => void;
  onSubmit?: () => void;
  customerDetails?: {
    customerName: string;
    customerSurName: string;
    emailInfo: string;
    phoneInfo: string;
  };
  policyDetailsHeader?: IPolicyDetailsHeader | null;
  policyVehicles?: IPolicyVehicle[];
  policyid?: string;
  lobCode?: string;
}

const ClaimFlow: React.FC<ClaimFlowProps> = ({
  onBack,
  customerDetails,
  policyDetailsHeader,
  policyVehicles,
  policyid,
  lobCode,
  onSubmit,
}) => {
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });

  const [activeStep, setActiveStep] = useState(0);
  const [selectedClaimType, setSelectedClaimType] = useState<
    "first-party" | "third-party" | null
  >(null);
  const [selectedClaimantType, setSelectedClaimantType] = useState<
    "self" | "others" | "agent" | null
  >(null);
  const [selectedDamageType, setSelectedDamageType] = useState<
    "vehicle" | "injury" | "both" | null
  >(null);
  const [formData, setFormData] = useState({
    firstName: customerDetails?.customerName || "",
    lastName: customerDetails?.customerSurName || "",
    email: customerDetails?.emailInfo || "",
    phone: customerDetails?.phoneInfo || "",
    policyNumber: policyDetailsHeader?.carrierPolicyNumber || "",
    insuredName: policyDetailsHeader?.insuredName || "",
    effectiveDate: policyDetailsHeader?.effectiveDate || "",
    expiryDate: policyDetailsHeader?.expiryDate || "",
    policyType: policyDetailsHeader?.policyType || "",
    role: "",
    driverType: "",
    relationshipToInsured: "",
    filingOnBehalf: "",
    insuredContact: "",
    dateOfLoss: "",
    timeOfLoss: "",
    reportedDate: new Date().toISOString().split("T")[0],
    reportedTime: new Date().toTimeString().slice(0, 5),
    incidentType: "",
    description: "",
    IncidentLocation: "",
    geoLocation: "",
    weatherConditions: "",
    policeReportFiled: false,
    policeReportNumber: "",
    authoritiesContacted: "",
    hasWitness: false,
    witnessName: "",
    witnessPhone: "",
    witnessEmail: "",
    witnessRelationship: "",
    vehicleYear: "",
    vehicleMake: "",
    vehicleModel: "",
    vin: "",
    vehicleLocation: "",
    vehicleDamageDescription: "",
    injuryType: null,
    injuryDescription: "",
    medicalProviderInfo: "",
    treatmentStatus: "",
    propertyType: "",
    propertyOwnerName: "",
    propertyOwnerContact: "",
    propertyDamageDescription: "",
    propertyDamagePhotos: [],
    vehicleDamagePhotos: [],
    confirmTrueDetails: false,
    selectedVehicle: null as IPolicyVehicle | null,
  });

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [reviewTab, setReviewTab] = useState(0);
  const [incidentErrors, setIncidentErrors] = useState({ dateError: false, timeError: false });

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prevStep) => prevStep - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (onBack) {
      onBack();
    }
  };

  const validatePhoneNumber = (phone: string) => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone.replace(/\D/g, ""));
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateAlphanumeric = (value: string) => {
    const regex = /^[a-zA-Z0-9]*$/;
    return regex.test(value);
  };

  const handleSubmit = () => {
    setSnackbar({
      open: true,
      message: "Claim submitted successfully",
      severity: "success",
    });

    setTimeout(() => {
      if (onSubmit) {
        onSubmit();
      }
    }, 1000);
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prevStep) => prevStep + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const handleClaimTypeSelect = (type: "first-party" | "third-party") => {
    setSelectedClaimType(type);
    setSelectedClaimantType(null);
    setFormData((prev) => ({
      ...prev,
      firstName: customerDetails?.customerName || "",
      lastName: customerDetails?.customerSurName || "",
      email: customerDetails?.emailInfo || "",
      phone: customerDetails?.phoneInfo || "",
      policyNumber: policyDetailsHeader?.carrierPolicyNumber || "",
      insuredName: policyDetailsHeader?.insuredName || "",
      effectiveDate: policyDetailsHeader?.effectiveDate || "",
      expiryDate: policyDetailsHeader?.expiryDate || "",
      policyType: policyDetailsHeader?.policyType || "",
      filingOnBehalf: "",
      insuredContact: "",
      role: "",
      dateOfLoss: "",
      timeOfLoss: "",
      driverType: "",
      witnessName: "",
      witnessPhone: "",
      witnessEmail: "",
      witnessRelationship: "",
      reportedDate: new Date().toISOString().split("T")[0],
      reportedTime: new Date().toTimeString().slice(0, 5),
      incidentType: "",
      description: "",
      IncidentLocation: "",
      geoLocation: "",
      weatherConditions: "",
      policeReportFiled: false,
      policeReportNumber: "",
      authoritiesContacted: "",
      vehicleYear: "",
      vehicleMake: "",
      vehicleModel: "",
      vin: "",
      vehicleLocation: "",
      vehicleDamageDescription: "",
      injuryType: null,
      injuryDescription: "",
      medicalProviderInfo: "",
      treatmentStatus: "",
      propertyType: "",
      propertyOwnerName: "",
      propertyOwnerContact: "",
      propertyDamageDescription: "",
      propertyDamagePhotos: [],
      vehicleDamagePhotos: [],
      confirmTrueDetails: false,
      selectedVehicle: null as IPolicyVehicle | null,
    }));
    handleNext();
  };

  const validateCurrentStep = () => {
    if (activeStep === 0) {
      return selectedClaimType !== null;
    }

    if (activeStep === 1) {
      const basicFieldsValid =
        formData.firstName.trim() !== "" &&
        formData.lastName.trim() !== "" &&
        formData.email.trim() !== "" &&
        validateEmail(formData.email) &&
        formData.phone.trim() !== "" &&
        validatePhoneNumber(formData.phone) &&
        formData.role.trim() !== "";

      // Policy information validation
      const policyInfoValid =
        validateAlphanumeric(formData.policyNumber) &&
        formData.policyType.trim() !== "";

      // Insured name validation based on claim type
      const insuredNameValid =
        selectedClaimType === "first-party"
          ? formData.insuredName.trim() !== ""
          : true; // Optional for third-party

      if (selectedClaimType === "third-party") {
        if (
          selectedClaimantType !== "self" &&
          selectedClaimantType !== "agent"
        ) {
          return false;
        }
      } else if (selectedClaimType === "first-party") {
        if (!["self", "others", "agent"].includes(selectedClaimantType || "")) {
          return false;
        }
        if (formData.role === "driver" && formData.driverType.trim() === "") {
          return false;
        }
      }

      if (
        selectedClaimantType === "agent" &&
        formData.filingOnBehalf.trim() === ""
      ) {
        return false;
      }

      if (
        selectedClaimantType === "others" &&
        formData.relationshipToInsured.trim() === ""
      ) {
        return false;
      }

      return basicFieldsValid && policyInfoValid && insuredNameValid;
    }

    if (activeStep === 2) {
      const today = new Date().toISOString().split("T")[0];
      const nowTime = new Date().toTimeString().slice(0, 5);
      const dateProvided = formData.dateOfLoss.trim() !== "";
      const timeProvided = formData.timeOfLoss.trim() !== "";
      const dateNotFuture = dateProvided && formData.dateOfLoss <= today;
      const timeNotFuture =
        timeProvided &&
        (
          (dateProvided && formData.dateOfLoss < today) ||
          (dateProvided && formData.dateOfLoss === today && formData.timeOfLoss <= nowTime)
        );
      const basicDetailsValid =
        dateProvided &&
        timeProvided &&
        dateNotFuture &&
        timeNotFuture &&
        formData.description.trim() !== "" &&
        formData.incidentType.trim() !== "" &&
        formData.IncidentLocation.trim() !== "";

      // Also block when child shows visible date/time errors
      if (incidentErrors.dateError || incidentErrors.timeError) {
        return false;
      }

      // Add validation for police report fields when policeReportFiled is true
      const policeReportValid = formData.policeReportFiled
        ? formData.policeReportNumber.trim() !== "" &&
          formData.authoritiesContacted.trim() !== ""
        : true;

      // Add validation for first-party claims
      if (selectedClaimType === "first-party") {
        const vehicleDetailsValid =
          formData.vin.trim() !== "" &&
          formData.vehicleLocation.trim() !== "";
        return basicDetailsValid && vehicleDetailsValid && policeReportValid;
      }

      if (
        selectedClaimType === "third-party" &&
        selectedDamageType === "both"
      ) {
        const vehicleDetailsValid =
          formData.vehicleYear.trim() !== "" &&
          formData.vehicleMake.trim() !== "" &&
          formData.vehicleModel.trim() !== "" &&
          formData.vin.trim() !== "" &&
          formData.vehicleDamageDescription.trim() !== "";

        // Add validation for injury details when bodily injury is selected
        const injuryDetailsValid =
          formData.injuryType === "bodily-injury"
            ? formData.injuryDescription.trim() !== ""
            : true;

        // Add validation for property damage details when property damage is selected
        const propertyDetailsValid =
          formData.injuryType === "property-damage"
            ? formData.propertyType.trim() !== "" &&
              formData.propertyDamageDescription.trim() !== ""
            : true;

        return (
          basicDetailsValid &&
          vehicleDetailsValid &&
          injuryDetailsValid &&
          propertyDetailsValid &&
          policeReportValid
        );
      }

      if (
        selectedClaimType === "third-party" &&
        selectedDamageType === "vehicle"
      ) {
        const vehicleDetailsValid =
          formData.vehicleYear.trim() !== "" &&
          formData.vehicleMake.trim() !== "" &&
          formData.vehicleModel.trim() !== "" &&
          formData.vin.trim() !== "" &&
          formData.vehicleDamageDescription.trim() !== "";
        return basicDetailsValid && vehicleDetailsValid && policeReportValid;
      }

      if (
        selectedClaimType === "third-party" &&
        (selectedDamageType === "injury" || selectedDamageType === "both")
      ) {
        // New logic: check which injuryType is selected
        let injuryFieldsValid = true;
        if (formData.injuryType === "bodily-injury") {
          injuryFieldsValid = formData.injuryDescription.trim() !== "";
        } else if (formData.injuryType === "property-damage") {
          injuryFieldsValid =
            formData.propertyType.trim() !== "" &&
            formData.propertyDamageDescription.trim() !== "";
        } else if (selectedDamageType === "both") {
          // If both, require both sets
          injuryFieldsValid =
            formData.injuryDescription.trim() !== "" &&
            formData.propertyType.trim() !== "" &&
            formData.propertyDamageDescription.trim() !== "";
        }
        return basicDetailsValid && injuryFieldsValid && policeReportValid;
      }

      return basicDetailsValid && policeReportValid;
    }

    if (activeStep === 3) {
      return true;
    }

    if (activeStep === 4) {
      return formData.confirmTrueDetails;
    }

    return true;
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <ClaimType
            selectedClaimType={selectedClaimType}
            onClaimTypeSelect={handleClaimTypeSelect}
          />
        );

      case 1:
        return (
          <PersonalInfo
            formData={formData}
            selectedClaimType={selectedClaimType}
            selectedClaimantType={selectedClaimantType}
            onFormChange={handleFormChange}
            onClaimantTypeSelect={setSelectedClaimantType}
            validatePhoneNumber={validatePhoneNumber}
            validateEmail={validateEmail}
            validateAlphanumeric={validateAlphanumeric}
          />
        );

      case 2:
        return (
          <IncidentDetails
            formData={formData}
            selectedClaimType={selectedClaimType}
            selectedDamageType={selectedDamageType}
            onFormChange={handleFormChange}
            onDamageTypeSelect={setSelectedDamageType}
            policyVehicles={policyVehicles}
            policyId={policyid}
            lobCode={lobCode}
            onValidationChange={setIncidentErrors}
          />
        );

      case 3:
        return (
          <Evidence
            uploadedFiles={uploadedFiles}
            onFilesUpload={setUploadedFiles}
          />
        );

      case 4:
        return (
          <Review
            formData={formData}
            selectedClaimType={selectedClaimType}
            selectedDamageType={selectedDamageType}
            uploadedFiles={uploadedFiles}
            reviewTab={reviewTab}
            onReviewTabChange={setReviewTab}
            onFormChange={handleFormChange}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={ClaimFlowStyles.container}>
      <Box sx={ClaimFlowStyles.header}>
        <IconButton
          id="Back-arrow-button"
          data-test-id="test-Back-arrow-button"
          aria-label="Back-arrow-button"
          onClick={handleBack}
          sx={ClaimFlowStyles.backButton}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={ClaimFlowStyles.title}>
          File a Claim
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label) => (
          <Step
            key={label}
            id={`${label}`}
            data-test-id={`test-${label}`}
            aria-label={`${label}`}
          >
            <StepLabel>
              <Typography sx={ClaimFlowStyles.stepLabel}>{label}</Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper sx={ClaimFlowStyles.paper}>{renderStepContent()}</Paper>

      <Box sx={ClaimFlowStyles.actions}>
        <Button
          variant="outlined"
          onClick={handleBack}
          disabled={activeStep === 0}
          sx={ClaimFlowStyles.button}
          id="Back-button"
          data-test-id="test-Back-button"
          aria-label="Back-button"
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
          disabled={!validateCurrentStep()}
          sx={ClaimFlowStyles.button}
          id={
            activeStep === steps.length - 1
              ? "submit_claim_button"
              : "next_button"
          }
          data-test-id={
            activeStep === steps.length - 1
              ? "test-submit_claim_button"
              : "test-next_button"
          }
          aria-label={
            activeStep === steps.length - 1
              ? "Submit Claim Button"
              : "Next Step Button"
          }
        >
          {activeStep === steps.length - 1 ? "Submit Claim" : "Next"}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={ClaimFlowStyles.alert}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClaimFlow;
